import React, { useEffect, useRef } from "react";
import { Player, Stage, Vector2, getFullPreviewSettings, type Project } from "@revideo/core";
import { settleSeek, shouldLoop, type PendingSeek } from "./reelClock.js";

/**
 * The reel's scene drawn live on a canvas by Revideo's core player — the way Revideo's own `<revideo-player>` element does
 * it, without that element or its React wrapper: the wrapper hands `variables` to the element as a property that only has a
 * getter, which React 19 cannot set, and the preview crashed on its first re-render. One player lives per mount; the props
 * change what and how it plays. The canvas is the stage's own buffer: the scene at its size, drawn at the box's pixel density.
 */
export interface ReelCanvasProps {
  project: Project;
  /** the scene's variables (`props` for the reel); a new object re-runs the scene */
  variables: Record<string, unknown>;
  /** the reel's frame rate: the clock the scene counts in */
  fps: number;
  playing: boolean;
  looping: boolean;
  speed: number;
  width: number;
  height: number;
  /** the player's time after each drawn frame, seconds */
  onTime: (seconds: number) => void;
  onDuration?: (seconds: number) => void;
  /** the player is up: what the preview may ask of it */
  onReady?: (reel: ReelHandle) => void;
}

/** What the preview may ask of the canvas player. */
export interface ReelHandle {
  /** moves the picture to a frame of the reel (a seek from the transcript, the timeline, the transport) */
  seek: (frame: number) => void;
}

/**
 * The scene at its own size and frame rate (the preview defaults would run it at 60), its buffer scaled down to the box's
 * pixels (never up): the canvas is then shown at the box's size.
 */
const settingsFor = (project: Project, fps: number, width: number) => {
  const base = getFullPreviewSettings(project);
  const dpr = typeof window !== "undefined" ? window.devicePixelRatio || 1 : 1;
  return { ...base, fps, size: new Vector2(base.size.x, base.size.y), resolutionScale: Math.min(1, (width * dpr) / base.size.x) };
};

export const ReelCanvas: React.FC<ReelCanvasProps> = ({ project, variables, fps, playing, looping, speed, width, height, onTime, onDuration, onReady }) => {
  const host = useRef<HTMLDivElement>(null);
  const playerRef = useRef<Player | null>(null);
  const stageRef = useRef<Stage | null>(null);
  // the latest callbacks and settings, read by the one render handler
  const callbacks = useRef({ onTime, onDuration, onReady }); callbacks.current = { onTime, onDuration, onReady };
  const initial = useRef({ variables, playing, looping, speed, width, height }); initial.current = { variables, playing, looping, speed, width, height };
  // real time: the core player draws one frame per turn and never drops one, so a frame slower than its slot would slow the
  // clip down (and pull the sound out of step); when it falls behind the wall clock it is seeked ahead to where the clock is
  const pace = useRef<{ wall: number; frame: number } | null>(null);
  const lastFrame = useRef(-1);
  // a seek asked for from outside that the player has not drawn yet
  const pending = useRef<PendingSeek | null>(null);

  useEffect(() => {
    const box = host.current;
    if (!box) return;
    const { variables: vars, playing: play, speed: rate, width: w } = initial.current;
    const stage = new Stage();
    const player = new Player(project);
    const settings = settingsFor(project, fps, w);
    stage.configure(settings);
    void player.configure(settings);
    player.setVariables(vars);
    // the loop is the studio's own (in the render handler below): the core's is switched off, because it also takes the end of
    // every recalculation pass (one follows each edit, and each play or pause) for the end of the clip and jumps to the start
    player.toggleLoop(false);
    player.setSpeed(rate);
    const canvas = stage.finalBuffer;
    canvas.style.width = "100%"; canvas.style.height = "100%"; canvas.style.display = "block";
    box.appendChild(canvas);
    let duration = -1;
    const render = async () => {
      await stage.render(player.playback.currentScene, player.playback.previousScene);
      const fps = player.playback.fps;
      const frame = player.playback.frame;
      if (player.playback.duration !== duration) { duration = player.playback.duration; callbacks.current.onDuration?.(duration / fps); }
      // a frame from before a seek that was asked for says nothing about where the clip now is: it is drawn, and that is all
      const settled = settleSeek(pending.current, frame, duration);
      pending.current = settled.pending;
      if (settled.stale) return;
      callbacks.current.onTime(frame / fps);
      if (shouldLoop({ playing: initial.current.playing, looping: initial.current.looping, finished: player.playback.finished, frame, duration })) {
        // the last frame of a looping clip: back to the first, still playing
        pace.current = null; lastFrame.current = 0;
        player.requestSeek(0);
        return;
      }
      if (initial.current.playing && !player.playback.finished) {
        // a jump (a seek, a loop) re-anchors the clock
        if (Math.abs(frame - lastFrame.current) > 2) pace.current = null;
        const anchor = pace.current ?? (pace.current = { wall: performance.now(), frame });
        const due = Math.floor(anchor.frame + ((performance.now() - anchor.wall) / 1000) * fps * initial.current.speed);
        if (due > frame + 1) { player.requestSeek(Math.min(due, player.playback.duration)); lastFrame.current = due; return; }
      } else pace.current = null;
      lastFrame.current = frame;
    };
    player.onRender.subscribe(render);
    player.activate();
    player.togglePlayback(play);
    playerRef.current = player; stageRef.current = stage;
    callbacks.current.onReady?.({ seek: (frame) => { pending.current = { frame, staleSeen: false }; pace.current = null; player.requestSeek(frame); } });
    return () => {
      player.onRender.unsubscribe(render);
      player.togglePlayback(false);
      player.deactivate();
      canvas.remove();
      playerRef.current = null; stageRef.current = null;
    };
  }, [project]);
  // an edit: the scene reads its props once when it runs, so new variables take effect through a reload, which re-runs the
  // scene and redraws the frame — a moment's work, so a burst of edits (typing) is settled first
  useEffect(() => {
    const t = setTimeout(() => { const p = playerRef.current; if (!p) return; p.setVariables(variables); p.playback.reload(); }, 150);
    return () => clearTimeout(t);
  }, [variables]);
  useEffect(() => { pace.current = null; playerRef.current?.togglePlayback(playing); }, [playing]);
  useEffect(() => { pace.current = null; playerRef.current?.setSpeed(speed); }, [speed]);
  useEffect(() => {
    const stage = stageRef.current, player = playerRef.current;
    if (!stage || !player) return;
    const settings = settingsFor(project, fps, width);
    stage.configure(settings);
    void player.configure(settings);
  }, [project, fps, width, height]);

  return <div ref={host} style={{ width, height, overflow: "hidden", background: "#0B0F0D" }} />;
};
